// Libraries
import { format, isValid, isSameDay, setMilliseconds, setSeconds } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';

export class DateHelper {
	static format(date: Date, formatString: string): string {
		// Convert moment format to date-fns format
		const dateFnsFormat = DateHelper.convertMomentFormatToDateFns(formatString);
		return format(date, dateFnsFormat);
	}

	static sameDay(date1: Date, date2: Date): boolean {
		return isSameDay(date1, date2);
	}

	static removeMilliseconds(date: Date): Date {
		return setMilliseconds(setSeconds(date, Math.floor(date.getSeconds())), 0);
	}

	public getMinutesFromCurrent(date: Date): number {
		if (!date || !isValid(date)) {
			return 0;
		}

		return new Date().getMinutes() - date.getMinutes();
	}

	static getTimeInUnix(date: Date) {
		// 👇️ timestamp in seconds (Unix timestamp)
		return Math.floor(date.getTime() / 1000);
	}
	
	static getDateInTimezone(
		utcMillis: number,
		utcOffset: number
	): Date {
		// Create a fake timezone string for the offset.
		const offsetHours: number = Math.abs(utcOffset);
		const offsetSign: '+' | '-' = utcOffset >= 0 ? '-' : '+';
		const fakeTimezone = `Etc/GMT${offsetSign}${offsetHours}`;
		
		return toZonedTime(utcMillis, fakeTimezone);
	}
	
	static formatDateInTimezone(
		utcMillis: number,
		utcOffset: number,
		formatString: string = 'M/d/yyyy HH:mm',
	): string {
		const zonedTime: Date = this.getDateInTimezone(utcMillis, utcOffset);
		return format(zonedTime, formatString);
	}
	
	// Helper method to convert moment format strings to date-fns format strings
	private static convertMomentFormatToDateFns(momentFormat: string): string {
		// Handle date-fns v3 format token changes
		let dateFnsFormat = momentFormat;

		// Year formats
		dateFnsFormat = dateFnsFormat.replace(/YYYY/g, 'yyyy');
		dateFnsFormat = dateFnsFormat.replace(/YY/g, 'yy');

		// Month formats
		dateFnsFormat = dateFnsFormat.replace(/MMMM/g, 'MMMM'); // Full month name
		dateFnsFormat = dateFnsFormat.replace(/MMM/g, 'MMM'); // Short month name
		dateFnsFormat = dateFnsFormat.replace(/MM/g, 'MM'); // Zero-padded month
		dateFnsFormat = dateFnsFormat.replace(/\bM\b/g, 'M'); // Month without padding

		// Day formats
		dateFnsFormat = dateFnsFormat.replace(/DD/g, 'dd'); // Zero-padded day
		dateFnsFormat = dateFnsFormat.replace(/\bD\b/g, 'd'); // Day without padding
		dateFnsFormat = dateFnsFormat.replace(/Do/g, 'do'); // Ordinal day (1st, 2nd, etc.)

		// Hour formats
		dateFnsFormat = dateFnsFormat.replace(/HH/g, 'HH'); // 24-hour format
		dateFnsFormat = dateFnsFormat.replace(/\bH\b/g, 'H'); // 24-hour format without padding
		dateFnsFormat = dateFnsFormat.replace(/hh/g, 'hh'); // 12-hour format
		dateFnsFormat = dateFnsFormat.replace(/\bh\b/g, 'h'); // 12-hour format without padding

		// Minute and second formats
		dateFnsFormat = dateFnsFormat.replace(/mm/g, 'mm'); // Minutes
		dateFnsFormat = dateFnsFormat.replace(/ss/g, 'ss'); // Seconds

		// Fix common problematic patterns
		dateFnsFormat = dateFnsFormat.replace(/^m\//g, 'M/'); // Fix 'm/d/yy' to 'M/d/yy'

		return dateFnsFormat;
	}
}
